import * as React from 'react';

import { ColumnListDto } from "@common/dto/column/list-dto";
import { IndividualListDto } from "@common/dto/individual/list-dto";
import { NewTableColumn, NewTableGetId, NewTableLoadMetadata, NewTableSortingDirection } from '@common/components/new-table';
import { HttpErrorDto } from '@common/dto/http';
import { I18n } from 'react-redux-i18n';
import { getTestAttributes } from '@common/utils/tests';
import { generateId } from '@common/utils/common';
import { GetIndividualsRequestParams } from '@common/actions/individuals';
import { QuestionnaireListDto } from '@common/dto/questionnaire/list-dto';
import { FormControl } from '@common/components/form-control';
import { FolderContext } from '@common/dto/folder/context';
import { UpdateDataDto } from '@common/dto/data/update-dto';
import { MODULE_NAME } from '@modules/invite-status/constants/shared';
import { InvitesGetIndividualsColumnsRequestParams } from '@modules/invite-status/services/invites';
import { InviteHistoryDto } from '@common/dto/invite-history/list-dto';
import { InviteStatusIndividual } from '@modules/invite-status/dto/individuals/list-dto';
import { InviteListDto } from '@common/dto/invite/list-dto';

export type IndividualChooserProps = {
  updateIndividual: (data: UpdateDataDto) => Promise<void>;
  questionnaire: QuestionnaireListDto;
  getIndividualsColumns: (params: InvitesGetIndividualsColumnsRequestParams) => Promise<Array<ColumnListDto>>;
  showErrorMessage: (message: string) => void;
  getHistoryLogs?: (params: {
    inviteId?: InviteListDto["id"];
    globalId: InviteStatusIndividual["id"];
    fhq4?: InviteListDto["fhq4"];
  }) => Promise<Array<InviteHistoryDto>>;
  getIndividuals: (params: GetIndividualsRequestParams) => Promise<{ data: Array<IndividualListDto>; totalCount: number }>;
  selectedIndividual?: IndividualListDto;
  onChange: (value?: {
    original: IndividualListDto; 
    computed: {
      firstName?: string; 
      lastName?: string; 
      email?: string 
    };
  }) => void;
};

export const useIndividualChooserController = (props: IndividualChooserProps) => {
  const { onChange, getIndividuals, getHistoryLogs, showErrorMessage, getIndividualsColumns, questionnaire, updateIndividual, selectedIndividual } = props;
  const [individualsTotalCount, setTotalCount] = React.useState<number>(0);
  const [individuals, setIndividuals] = React.useState<Array<IndividualListDto>>([]);
  const [individualsLoadMetadata, setIndividualsLoadMetadata] = React.useState<NewTableLoadMetadata>({ pagination: { size: 45, start: 0, } });
  const [loadingIndividuals, setLoadingIndividuals] = React.useState(false);
  const [loadingHistoryLogs, setLoadingHistoryLogs] = React.useState(false);
  const [historyLogs, setHistoryLogs] = React.useState<Array<InviteHistoryDto>>([]);
  const [individualsColumns, setIndividualsColumns] = React.useState<Array<ColumnListDto>>([]);
  const [searchKeyword, setSearchKeyword] = React.useState<string>('');

  const itemUniqProperty = 'localItemItemIdProperty';

  const getColumnId = React.useCallback((column: ColumnListDto) => {
    return `field_id__${column.field.id.toString()}__field_type__${column.field.type.toString()}__context__${column.field.context.toString()}`;
  }, []);

  const handleLoadData = async (params: NewTableLoadMetadata & {
    columns: Array<ColumnListDto>;
    loadedIndividuals: Array<IndividualListDto>;
    search?: string;
  }) => {
    try {
      setLoadingIndividuals(true);

      const { filtering, pagination, sorting, search } = params;

      const columns = params.columns.map((x): ColumnListDto => {
        const isSorted = getColumnId(x) === sorting?.id;
  
        return {
          ...x,
          sortOrder: isSorted ? 1 : 0,
          sortAsc: isSorted ? sorting?.direction === NewTableSortingDirection.asc : false,
        };
      });

      const { data, totalCount } = await getIndividuals({
        pagination,
        filtering: {
          filterApplied: false,
          columns,
          search,
        },
      });

      const processedData = data.map((x) => {
        (x as Record<string, any>)[itemUniqProperty] = generateId();

        return x;
      });

      const finalData = pagination.start > 0
        ? [...params.loadedIndividuals, ...processedData]
        : processedData;

      setIndividuals(finalData);
      setTotalCount((x) => pagination.start > 0 ? x : totalCount);
      setIndividualsLoadMetadata({
        pagination,
        sorting,
        filtering
      });
    } finally {
      setLoadingIndividuals(false);
    }
  };

  const getAndSetIndividualsColumns = React.useCallback(async () => {
    const cols = await getIndividualsColumns({
      questionnaireId: questionnaire.questionnaireId,
      fhq4: questionnaire.fhq4,
    });
    const processedResult = cols.filter((x) => x.field);
    setIndividualsColumns(processedResult);

    return processedResult;
  }, []);

  const initialize = async () => {
    try {
      const individualsColumns = await getAndSetIndividualsColumns();
      await handleLoadData({
        columns: individualsColumns,
        loadedIndividuals: [],
        pagination: individualsLoadMetadata.pagination,
        sorting: individualsLoadMetadata.sorting,
        search: searchKeyword,
      });
    } catch (err) {
      console.error(err);
      const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get data');
      showErrorMessage(errorMessage);
    }
  };

  React.useEffect(() => {
    initialize();
  }, []);

  const handleSearch = async ({ searchKeyword }: { searchKeyword: string }) => {
    try {
      setSearchKeyword(searchKeyword);
      await handleLoadData({
        columns: individualsColumns,
        loadedIndividuals: [],
        pagination: { ...individualsLoadMetadata.pagination, start: 0 },
        sorting: individualsLoadMetadata.sorting,
        search: searchKeyword,
      });

      setHistoryLogs([]);
      onChange(undefined);
    } catch (err) {
      console.error(err);
      const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get data');
      showErrorMessage(errorMessage);
    }
  };

  const INDIVIDUAL_FIELD_THAT_IS_SAME_ON_ALL_ENVS = {
    name: {
      id: 18,
      system: true,
      context: FolderContext.individual,
    },
    firstName: {
      id: 94
    },
    lastName: {
      id: 95
    },
    email: {
      id: 115
    },
  };

  const getAttributesFromIndividual = React.useCallback((individual: IndividualListDto) => {
    const individualAsObject = individual as unknown as Record<string, string>;

    return {
      firstName: individualAsObject[INDIVIDUAL_FIELD_THAT_IS_SAME_ON_ALL_ENVS.firstName.id],
      lastName: individualAsObject[INDIVIDUAL_FIELD_THAT_IS_SAME_ON_ALL_ENVS.lastName.id],
      email: individualAsObject[INDIVIDUAL_FIELD_THAT_IS_SAME_ON_ALL_ENVS.email.id],
    };
  }, []);

  const handleSelectedItemsChange = React.useCallback(async (items: Array<IndividualListDto>) => {
    if (items.length) {
      const individual = items[0]!;

      onChange({
        original: individual,
        computed: getAttributesFromIndividual(individual),
      });

      if (getHistoryLogs && items[0]) {
        try {
          setLoadingHistoryLogs(true);

          const res = await getHistoryLogs({ globalId: items[0].id });
          setHistoryLogs(res);
        } catch (e) {
          console.error(e);
          showErrorMessage((e as HttpErrorDto | undefined)?.entity || I18n.t('Failed to load individual history logs'));
        } finally {
          setLoadingHistoryLogs(false);
        }
      }
    }
  }, [onChange, getHistoryLogs, getAttributesFromIndividual, showErrorMessage]);

  const getId = React.useMemo((): NewTableGetId => (
    {
      header: () => getTestAttributes({ id: "bw-quest-invites-step4-sel-ind-list-thead-row0" }).id,
      headerCell: (cell, index) => getTestAttributes({ id: `bw-quest-invites-step4-sel-ind-list-thead-row0-cell${index}` }).id,
      bodyRow: (row, index) => getTestAttributes({ id: `bw-quest-invites-step4-sel-ind-list-tbody-row${index}` }).id,
      bodyCell: (row, rowIndex, cell, cellIndex) => getTestAttributes({ id: `bw-quest-invites-step4-sel-ind-list-tbody-row${rowIndex}-cell${cellIndex}` }).id,
    }
  ), []);

  const handleFieldChange = React.useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { columnId } = e.target.dataset;
    const value = e.target.value.trim();

    const column = individualsColumns.find((x) => getColumnId(x) === columnId);

    if (!column || !selectedIndividual) {
      return;
    }

    const data: UpdateDataDto = {
      globalId: Number(selectedIndividual.id),
      fields: [
        {
          context: column.field.context,
          fieldId: column.field.id,
          system: Number(column.field.system) as 0 | 1,
          value,
        }
      ]
    };

    try {
      await updateIndividual(data);
      const updatedIndividual = {
        ...selectedIndividual,
        [column.field.id]: value,
      };

      const updatedIndividuals = individuals.map((x) => {
        if (x.id.toString() === selectedIndividual.id.toString()) {
          return updatedIndividual;
        }

        return x;
      });
      
      setIndividuals(updatedIndividuals);
      onChange({
        computed: getAttributesFromIndividual(updatedIndividual),
        original: updatedIndividual
      });
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto)?.entity || I18n.t('Failed to update individual'));
    }
  }, [
    updateIndividual, 
    individualsColumns, 
    individuals,
    getColumnId, 
    onChange, 
    getAttributesFromIndividual,
    selectedIndividual,
  ]);

  const renderInput = React.useCallback((
    { row: { original }, column }: 
    { row: { original: IndividualListDto }; column: { id: string; columnDef: { accessorKey: string } } }
  ) => {
    return (
      <FormControl 
        {...getTestAttributes(
          `${MODULE_NAME}_invite_patient_ind_chooser`,
          'table_cell',
          'input',
          `${column.columnDef.accessorKey}_${original.id}`
        )}
        className="InviteStatusIndividualChooser__table__input"
        defaultValue={(original as unknown as Record<string, string>)[column.columnDef.accessorKey]}
        data-column-id={column.id}
        onBlur={handleFieldChange}
      />
    );
  }, [handleFieldChange]);

  const processedColumns = React.useMemo(() => {
    return individualsColumns.map((x): NewTableColumn<IndividualListDto> => {
      const isIndividualNameColumn = (
        x.field.id === INDIVIDUAL_FIELD_THAT_IS_SAME_ON_ALL_ENVS.name.id 
        && x.field.system === INDIVIDUAL_FIELD_THAT_IS_SAME_ON_ALL_ENVS.name.system
        && x.field.context === INDIVIDUAL_FIELD_THAT_IS_SAME_ON_ALL_ENVS.name.context
      );

      return {
        id: getColumnId(x),
        accessorKey: x.field.id.toString(),
        minSize: 20,
        size: 200,
        header: x.field.name,
        disableActions: true,
        enableResizing: true,
        enableSorting: false,
        cell: (isIndividualNameColumn ? undefined : renderInput) as NewTableColumn<IndividualListDto>['cell'],
      };
    });
  }, [individualsColumns, renderInput, getColumnId]);

  return {
    handleSearch,
    individualsColumns,
    processedColumns,
    individualsLoadMetadata,
    loadingIndividuals,
    loadingHistoryLogs,
    individuals,
    historyLogs,
    handleSelectedItemsChange,
    getId,
    itemUniqProperty,
    handleLoadData,
    individualsTotalCount
  };
};